<script lang="ts">
	/* eslint-disable no-alert */
	import { createPromiseStore } from "../../../../core/utils/createPromiseStore.js";
	import type { UploadedFile } from "../../../../core/schema/UploadedFile.js";
	import { maxFileSize } from "../../../config/maxFileSize.js";
	import { createEventDispatcher } from "svelte";
	import Button from "../Button.svelte";
	import DynamicPicture from "../Picture/DynamicPicture.svelte";
	import Icon from "../Icon.svelte";
	import uploadIcon from "../../../../core/assets/icons/upload.svg?raw";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import { putObjectToUpload } from "../../api/s3.js";
	import { isFileImage } from "../../../../core/utils/isFileImage.js";
	import translations from "./FileUploader.translations.json";
	import { getTranslate } from "../../utils/getTranslate.js";
	import { nbspify } from "../../../../core/utils/nbspify.js";

	export let destinationFolder: string;
	export let multiple = false;
	export let disabled = false;
	export let currentFiles: UploadedFile[] = [];
	export let accept: string | string[] = "*/*";
	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	export let maximum: number = multiple ? Infinity : 1;
	// eslint-disable-next-line @typescript-eslint/prefer-as-const
	export let bucket: "upload" = "upload"; // Add more buckets if needed
	export let required = false;
	export let white = false;
	export let round = false;
	export let noBorder = false;

	const dispatch = createEventDispatcher();
	const globalSettings = getGlobalSettings();
	const { PUBLIC_GRAPHQL_ENDPOINT } = globalSettings;
	const translate = getTranslate(translations);

	$: uploadFunction = {
		async upload(key: string, file: File | Blob): Promise<UploadedFile> {
			return await putObjectToUpload(PUBLIC_GRAPHQL_ENDPOINT, file, key);
		},
	}[bucket];

	let alreadyUploaded: [File, UploadedFile][] = [];

	const upload = createPromiseStore(async (filesToUpload: File[]) => {
		return await Promise.all(
			Array.from(filesToUpload).map(async (file) =>
				uploadFunction(destinationFolder, file).then((s3File) => {
					alreadyUploaded.push([file, s3File]);
					return s3File;
				}),
			),
		);
	});

	let input: HTMLInputElement | undefined = undefined;
	let fileList: FileList | undefined;

	function onChange(files: UploadedFile[]): void {
		dispatch("change", { files: multiple ? files : files[0] });
	}

	onChange(currentFiles);

	async function uploadFiles(fileList: FileList): Promise<void> {
		try {
			const filesToUpload = Array.from(fileList).filter(
				(file) => !alreadyUploaded.some(([f]) => `${f.name}.${f.type}` === `${file.name}.${f.type}`),
			);

			for (const file of filesToUpload) {
				if (file.size > maxFileSize) {
					alert(
						`Soubor ${file.name} je příliš velký (${Math.round(file.size / 1024 / 1024)} MB). Maximální povolená velikost souboru je ${maxFileSize / 1024 / 1024} MB.`,
					);
					return;
				}

				const allowedFileTypes = Array.isArray(accept) ? accept : accept.split(",");
				if (accept !== "*/*" && !allowedFileTypes.some((type) => file.type.startsWith(type))) {
					alert(`Soubor '${file.name}' má nepodporovaný typ.`);
					return;
				}
			}

			const files = await upload.call(filesToUpload);
			if (multiple) {
				currentFiles = [...currentFiles, ...files];
			} else {
				currentFiles = files;
			}
			onChange(currentFiles);
		} finally {
			if (input) {
				input.value = "";
			}
		}
	}

	$: {
		if (fileList) {
			void uploadFiles(fileList);
		}
	}

	function onDelete(file: UploadedFile): void {
		if (input) {
			input.value = "";
		}
		currentFiles = currentFiles.filter((search) => search.path !== file.path);
		onChange(currentFiles);
	}

	$: {
		alreadyUploaded = alreadyUploaded.filter(([_, s3File]) => currentFiles.includes(s3File));
	}

	function onInputChange({ target }: Event): void {
		const input = target as HTMLInputElement;
		if (input.files) {
			void uploadFiles(input.files);
		}
	}

	$: isDisabled = disabled || $upload.status === "loading";
	let loadingInput: HTMLInputElement | null = null;
	$: {
		if (loadingInput) {
			loadingInput.setCustomValidity(translate("waitForLoad"));
		}
	}

	// eslint-disable-next-line no-warning-comments
	// FIXME remove this after upgrade to Svelte 5.
	/* eslint-disable @typescript-eslint/explicit-function-return-type */
</script>

<div class="relative flex flex-col gap-4 {white && 'bg-white'} {round && 'rounded-lg'}">
	{#if $upload.status === "loading"}
		<input
			aria-label={translate("waitForLoad")}
			bind:this={loadingInput}
			type="text"
			required
			class="absolute opacity-0"
			tabindex="-1"
		/>
	{/if}
	{#if currentFiles.length < maximum}
		<div
			class="relative flex h-32 w-full items-center justify-center rounded-lg {!noBorder &&
				'border-2 border-dashed'} p-4 lg:px-10 lg:py-4 lg:pt-10"
		>
			<input
				aria-label={translate("uploader")}
				bind:this={input}
				type="file"
				class="absolute inset-0 h-full opacity-0"
				class:cursor-pointer={!isDisabled}
				class:cursor-not-allowed={isDisabled}
				accept={Array.isArray(accept) ? accept.join(",") : accept}
				disabled={isDisabled}
				{multiple}
				on:change={onInputChange}
				{required}
			/>
			<div class="pointer-events-none flex h-full w-full items-center">
				{#if $upload.status === "loading"}
					<span class="text-2xs mx-auto">{translate("uploading")}</span>
				{:else}
					<div class="grid w-full grid-rows-[1fr,2fr,1fr] gap-2">
						<slot />
						<Icon class="mx-auto h-[2.875rem] " icon={uploadIcon} />
					</div>
				{/if}
			</div>
		</div>
	{/if}
	{#if currentFiles.length > 0}
		<div class="flex flex-col gap-2 p-4">
			{#each currentFiles as file (file.path)}
				<div class="flex items-center gap-2">
					{#if isFileImage(file)}
						<DynamicPicture
							image={file}
							imgClass="aspect-square w-24 object-contain"
							alt={file.title ?? translate("thumbnail")}
							lightboxId={file.path}
							lightboxWidth={600}
							height={96}
							width={96}
							{bucket}
						/>
					{/if}
					<span class="max-w-48 overflow-clip text-ellipsis">{nbspify(file.title)}</span>
					<Button
						aria-label={translate("remove")}
						class="ml-auto"
						variant="transparent"
						on:click={() => {
							onDelete(file);
						}}
					>
						×
					</Button>
				</div>
			{/each}
		</div>
	{/if}
</div>
